import { useState, useEffect, useCallback, useRef } from 'react';

import { Dialog, DialogContent, DialogProps, IconButton, Fade } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { Maybe, ButtonPopoverContentCollection } from '@/types/generated';
import { ComponentResolver, ComponentProps } from '@/components/ComponentResolver';
import Transition from '@/components/TransitionComonent';

import { popoverContentStyles, iconButtonStyles } from './TeamTagDialog.styles';
export interface ITeamTAGDialog extends DialogProps {
	/**
	 * Whether the dialog is open or not.
	 *
	 * @type {boolean}
	 * @memberof ITeamTAGDialog
	 */
	open: boolean;
	/**
	 * Callback to be called when the dialog is closed.
	 *
	 * @memberof ITeamTAGDialog
	 */
	handleClose: () => void;
	/**
	 * collection of content to be displayed in the dialog.
	 *
	 * @memberof ITeamTAGDialog
	 */
	contentCollection?: Maybe<ButtonPopoverContentCollection>;
}
export default function TeamTagDialog({ open, contentCollection, handleClose }: Readonly<ITeamTAGDialog>) {
	const [index, setIndex] = useState(0);
	const contentRef = useRef<HTMLDivElement>(null);

	const handlePrev = useCallback(() => {
		setIndex((prev) => {
			const length = contentCollection?.items?.length ?? 0;
			return (prev - 1 + length) % length;
		});
	}, [contentCollection?.items?.length]);

	const handleNext = useCallback(() => {
		setIndex((prev) => (prev + 1) % (contentCollection?.items.length ?? 0));
	}, [contentCollection?.items.length]);

	// Re-wire arrows each time new content is rendered
	useEffect(() => {
		if (contentRef.current) {
			contentRef.current.scrollTop = 0;
		}

		const timer = setTimeout(() => {
			const prevEl = document.getElementById('nav-prev');
			const nextEl = document.getElementById('nav-next');

			if (prevEl)
				prevEl.onclick = (e) => {
					e.preventDefault();
					handlePrev();
				};
			if (nextEl)
				nextEl.onclick = (e) => {
					e.preventDefault();
					handleNext();
				};

			return () => {
				if (prevEl) prevEl.onclick = null;
				if (nextEl) nextEl.onclick = null;
			};
		}, 1000);
		return () => clearTimeout(timer);
	}, [index, open, handleNext, handlePrev]);

	return (
		<Dialog
			data-test-id="dialog_contentful_button_popover"
			open={open}
			scroll="paper"
			TransitionComponent={Transition}
			onClose={() => {
				handleClose();
				setIndex(0);
			}}
			sx={{ ...popoverContentStyles }}
			aria-labelledby="popover-dialog-title"
			aria-describedby="popover-dialog-description"
		>
			<IconButton
				aria-label="close"
				onClick={() => {
					handleClose();
					setIndex(0);
				}}
				sx={{ ...iconButtonStyles }}
				data-test-id="button_close_dialog"
			>
				<CloseIcon sx={{ color: '#B9DEFA' }} />
			</IconButton>
			<Fade in key={index} timeout={500}>
				<DialogContent ref={contentRef} data-test-id="text_dialog_text_content" sx={{ padding: '0' }}>
					<ComponentResolver
						key={index}
						componentProps={{
							...(contentCollection?.items[index] as ComponentProps),
						}}
						typename={contentCollection?.items[index]?.__typename || ''}
					/>
				</DialogContent>
			</Fade>
		</Dialog>
	);
}
